"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { useFirestoreDoc, useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { LinkPreviewCard } from "@/components/ui/LinkPreviewCard";
import { ReactionBar } from "@/components/ui/ReactionBar";
import { ReactionDetailsSheet } from "@/components/ui/ReactionDetailsSheet";
import { DownloadableImage } from "@/components/ui/DownloadableImage";
import { MentionText } from "@/components/ui/MentionText";
import { relativeDate } from "@/lib/humanDate";
import { fromTimestamp, toTimestamp, updateDocument } from "@/lib/firestore";
import { markEdited, isEdited } from "@/lib/postEdit";
import { resolveMentions } from "@/lib/mentionParser";
import { togglePin as togglePinPost, deletePost } from "@/lib/postActions";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  addDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import type { HouseholdPost, HouseholdPostReply } from "@/lib/models";
import { Link, useNavigate } from "react-router-dom";

interface PostDetailProps {
  postId: string;
}

export function PostDetail({ postId }: PostDetailProps) {
  const { householdId, members, profile } = useHouseholdContext();
  const t = useTranslations();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editMessage, setEditMessage] = useState("");
  // Reply edit state — only one reply can be edited at a time. Mirrors
  // the iOS reply edit menu in `PostDetailView.swift`.
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [replyImage, setReplyImage] = useState<File | null>(null);
  const [replyImagePreview, setReplyImagePreview] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [showReactorsSheet, setShowReactorsSheet] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLInputElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);

  const postPath = householdId ? `households/${householdId}/posts` : null;
  const { data: post, loading: postLoading } =
    useFirestoreDoc<HouseholdPost>(postPath, postId);

  // Auto-focus the reply composer when the post loads. Mirrors iOS
  // PostDetailView's focusComposer pattern — gives the keyboard immediately
  // for the common case of viewing a post to reply.
  useEffect(() => {
    if (!post) return;
    replyInputRef.current?.focus();
  }, [post]);

  const repliesPath =
    householdId && postId
      ? `households/${householdId}/posts/${postId}/replies`
      : null;
  const { data: replies, loading: repliesLoading } =
    useFirestoreCollection<HouseholdPostReply>(repliesPath);

  const sortedReplies = useMemo(() => {
    return [...replies].sort((a, b) => {
      const aTime = fromTimestamp(a.created_at)?.getTime() ?? 0;
      const bTime = fromTimestamp(b.created_at)?.getTime() ?? 0;
      return aTime - bTime;
    });
  }, [replies]);

  const findMember = (uid: string) => members.find((m) => m.uid === uid);
  const isCreator = post?.created_by === profile?.uid;

  // ── Reactions ──────────────────────────────────────────────────────────

  const handleToggleReaction = async (emojiKey: string) => {
    if (!householdId || !postId || !profile?.uid) return;
    const currentReactions = post?.reactions?.[emojiKey] ?? [];
    const alreadyReacted = currentReactions.includes(profile.uid);

    await updateDocument(`households/${householdId}/posts`, postId, {
      [`reactions.${emojiKey}`]: alreadyReacted
        ? arrayRemove(profile.uid)
        : arrayUnion(profile.uid),
    });
  };

  // ── Pin / Unpin ────────────────────────────────────────────────────────

  const handleTogglePin = async () => {
    if (!householdId || !postId || !profile?.uid) return;
    setShowActions(false);
    await togglePinPost({
      householdId,
      postId,
      pinnedBy: profile.uid,
      isPinned: post?.is_pinned ?? false,
    });
  };

  // ── Edit Post ──────────────────────────────────────────────────────────

  const startEditing = () => {
    setEditTitle(post?.post_title ?? "");
    setEditMessage(post?.post_message ?? "");
    setEditing(true);
    setShowActions(false);
  };

  const handleSaveEdit = async () => {
    if (!householdId || !postId || !editTitle.trim()) return;
    // Re-resolve `@nickname` tokens so the backend `post_mention` push
    // fires for any newly added mentions on edit. Mirrors iOS
    // `AddEditPostView.swift:269`.
    const mentions = resolveMentions(editMessage, members);
    await updateDocument(
      `households/${householdId}/posts`,
      postId,
      markEdited({
        post_title: editTitle.trim(),
        post_message: editMessage.trim() || null,
        mentions,
      }),
    );
    setEditing(false);
  };

  // ── Edit Reply ─────────────────────────────────────────────────────────

  const startEditingReply = (reply: HouseholdPostReply) => {
    if (!reply.uid) return;
    setEditingReplyId(reply.uid);
    setEditReplyText(reply.text);
  };

  const cancelEditingReply = () => {
    setEditingReplyId(null);
    setEditReplyText("");
  };

  const handleSaveReplyEdit = async () => {
    if (!householdId || !postId || !editingReplyId || !editReplyText.trim()) {
      return;
    }
    await updateDocument(
      `households/${householdId}/posts/${postId}/replies`,
      editingReplyId,
      // Mirrors `ZoeyRepository.editPostReply(...)` (`:804`).
      markEdited({
        text: editReplyText.trim(),
      }),
    );
    setEditingReplyId(null);
    setEditReplyText("");
  };

  // ── Delete Post ────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!householdId || !postId) return;
    await deletePost(householdId, postId);
    navigate("/posts");
  };

  // ── Submit Reply ───────────────────────────────────────────────────────

  const handleReplyImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplyImage(file);
    setReplyImagePreview(URL.createObjectURL(file));
  };

  const clearReplyImage = () => {
    setReplyImage(null);
    if (replyImagePreview) URL.revokeObjectURL(replyImagePreview);
    setReplyImagePreview(null);
    if (replyFileInputRef.current) replyFileInputRef.current.value = "";
  };

  const handleShare = async () => {
    if (!post) return;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const title = post.post_title;
    const text = post.post_message ?? title;
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error("Failed to share:", err);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasText = replyText.trim().length > 0;
    const hasImage = !!replyImage;
    if ((!hasText && !hasImage) || !householdId || !postId || !profile?.uid || submitting)
      return;

    setSubmitting(true);
    try {
      let imageUrl: string | undefined;
      if (replyImage) {
        const { compressForContent } = await import("@/lib/imageCompressor");
        const compressed = await compressForContent(replyImage);
        const fileName = crypto.randomUUID();
        const imageRef = ref(
          storage,
          `posts/${householdId}/${postId}/replies/${fileName}.jpg`
        );
        await uploadBytes(imageRef, compressed);
        imageUrl = await getDownloadURL(imageRef);
      }

      const repliesRef = collection(
        db,
        `households/${householdId}/posts/${postId}/replies`
      );
      const replyData: Record<string, unknown> = {
        created_by: profile.uid,
        created_at: toTimestamp(new Date()),
        text: replyText.trim(),
      };
      if (imageUrl) replyData.imageUrl = imageUrl;
      await addDoc(repliesRef, replyData);

      const lastReplyText = hasText ? replyText.trim() : "Sent an image";
      await updateDocument(`households/${householdId}/posts`, postId, {
        num_replies: (post?.num_replies ?? 0) + 1,
        last_reply_uid: profile.uid,
        last_reply_text: lastReplyText,
        last_reply_at: toTimestamp(new Date()),
      });

      setReplyText("");
      clearReplyImage();
    } catch (error) {
      console.error("Failed to submit reply:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (postLoading || repliesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="p-4">
        <p className="text-gray-500">{t("posts.detail.notFound")}</p>
        <Link to="/posts" className="text-blue-500 text-sm mt-2 inline-block">
          {t("posts.backToPosts")}
        </Link>
      </div>
    );
  }

  const author = findMember(post.created_by);
  const postDate = fromTimestamp(post.created_at);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-white/10 flex items-center justify-between">
        <Link
          to="/posts"
          className="text-blue-500 text-sm flex items-center gap-1"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {t("posts.backToPosts")}
        </Link>

        {/* Actions menu */}
        <div className="relative" ref={actionsRef}>
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-500"
            aria-label={t("posts.actions.aria")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>

          {showActions && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowActions(false)}
              />
              <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-white/10 overflow-hidden min-w-[160px]">
                {isCreator && (
                  <button
                    onClick={startEditing}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    {t("posts.actions.edit")}
                  </button>
                )}
                <button
                  onClick={handleTogglePin}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  {post.is_pinned ? t("posts.actions.unpin") : t("posts.actions.pin")}
                </button>
                <button
                  onClick={() => {
                    setShowActions(false);
                    void handleShare();
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  {shareCopied ? t("posts.actions.linkCopied") : t("posts.actions.share")}
                </button>
                {isCreator && (
                  <button
                    onClick={() => {
                      setShowActions(false);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    {t("posts.actions.delete")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Post Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <Card>
          {/* Pin badge */}
          {post.is_pinned && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
              </svg>
              {t("posts.pinned")}
            </div>
          )}

          <div className="flex items-start gap-3 mb-3">
            <Avatar
              src={author?.profile_url}
              alt={author?.nickname ?? author?.name ?? ""}
              size={44}
            />
            <div>
              <p className="font-semibold text-sm">
                {author?.nickname ?? author?.name ?? t("posts.author.unknown")}
              </p>
              {postDate && (
                <p className="text-xs text-gray-400">
                  {relativeDate(postDate)}
                  {isEdited(post) && (
                    <>
                      {" "}
                      <span className="italic">{t("posts.edited")}</span>
                    </>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Edit mode */}
          {editing ? (
            <div className="space-y-3">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 text-base font-bold outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
              <textarea
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 text-sm outline-none resize-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-1.5 rounded-full text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
                >
                  {t("posts.edit.cancel")}
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editTitle.trim()}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[color:var(--accent-color)] text-white disabled:opacity-50"
                >
                  {t("posts.edit.save")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold mb-2">{post.post_title}</h1>

              {post.post_message && (
                <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                  <MentionText text={post.post_message} members={members} />
                </p>
              )}

              {/* Link preview */}
              <LinkPreviewCard text={post.post_message} />
            </>
          )}

          {post.images && post.images.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {post.images.map((url, i) => (
                <DownloadableImage
                  key={i}
                  src={url}
                  alt={t("posts.detail.imageAlt", { n: i + 1 })}
                  className="rounded-lg overflow-hidden max-h-64"
                />
              ))}
            </div>
          )}

          {/* Reactions */}
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/10">
            <ReactionBar
              reactions={post.reactions}
              currentUserId={profile?.uid}
              onToggleReaction={handleToggleReaction}
              addReactionAriaLabel={t("posts.reactions.add")}
              showReactorsAriaLabel={t("posts.reactions.viewWho")}
              onShowReactors={() => setShowReactorsSheet(true)}
              reactionLabels={{
                love: t("posts.reactions.love"),
                haha: t("posts.reactions.haha"),
                celebrate: t("posts.reactions.celebrate"),
                wow: t("posts.reactions.wow"),
                sad: t("posts.reactions.sad"),
                fire: t("posts.reactions.fire"),
              }}
            />
          </div>

          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span>
              {t(
                post.num_replies === 1
                  ? "posts.replies.one"
                  : "posts.replies.other",
                { count: post.num_replies },
              )}
            </span>
          </div>
        </Card>

        {/* Replies */}
        {sortedReplies.length > 0 && (
          <div className="space-y-2">
            {sortedReplies.map((reply) => {
              const replyAuthor = findMember(reply.created_by);
              const replyDate = fromTimestamp(reply.created_at);
              const isOwnReply = reply.created_by === profile?.uid;
              const isEditingThisReply = editingReplyId === reply.uid;

              return (
                <Card key={reply.uid}>
                  <div className="flex items-start gap-3">
                    <Avatar
                      src={replyAuthor?.profile_url}
                      alt={replyAuthor?.nickname ?? replyAuthor?.name ?? ""}
                      size={32}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm">
                          {replyAuthor?.nickname ??
                            replyAuthor?.name ??
                            t("posts.author.unknown")}
                        </p>
                        {replyDate && (
                          <span className="text-xs text-gray-400">
                            {relativeDate(replyDate)}
                            {isEdited(reply) && (
                              <>
                                {" "}
                                <span className="italic">
                                  {t("posts.edited")}
                                </span>
                              </>
                            )}
                          </span>
                        )}
                      </div>
                      {isEditingThisReply ? (
                        <div className="mt-1 space-y-2">
                          <textarea
                            value={editReplyText}
                            onChange={(e) => setEditReplyText(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/10 text-sm outline-none resize-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={cancelEditingReply}
                              className="px-3 py-1 rounded-full text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
                            >
                              {t("posts.edit.cancel")}
                            </button>
                            <button
                              onClick={handleSaveReplyEdit}
                              disabled={!editReplyText.trim()}
                              className="px-3 py-1 rounded-full text-xs font-semibold bg-[color:var(--accent-color)] text-white disabled:opacity-50"
                            >
                              {t("posts.edit.save")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {reply.text && (
                            <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">
                              <MentionText text={reply.text} members={members} />
                            </p>
                          )}
                          {reply.imageUrl && (
                            <DownloadableImage
                              src={reply.imageUrl}
                              alt={t("posts.detail.imageAlt", { n: 1 })}
                              className="mt-1 max-w-xs max-h-64 rounded-lg overflow-hidden"
                            />
                          )}
                          {/* Compact link preview in replies */}
                          <LinkPreviewCard text={reply.text} compact />
                          {isOwnReply && (
                            <button
                              onClick={() => startEditingReply(reply)}
                              className="mt-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                              {t("posts.actions.edit")}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Reply Input */}
      <form
        onSubmit={handleSubmitReply}
        className="p-4 border-t border-gray-100 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur-sm space-y-2"
      >
        {replyImagePreview && (
          <div className="relative inline-block">
            <img
              src={replyImagePreview}
              alt={t("common.selectedImageAlt")}
              className="max-h-24 rounded-lg object-cover"
            />
            <button
              type="button"
              onClick={clearReplyImage}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-600 text-white text-xs flex items-center justify-center hover:bg-gray-700"
              aria-label={t("posts.reply.removeImage")}
            >
              ×
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => replyFileInputRef.current?.click()}
            className="shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-500"
            aria-label={t("chats.detail.attachImage")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </button>
          <input
            ref={replyFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleReplyImageSelect}
            className="hidden"
          />
          <input
            ref={replyInputRef}
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t("posts.reply.placeholder")}
            className="flex-1 px-4 py-2 rounded-full bg-gray-100 dark:bg-white/10 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
          />
          <button
            type="submit"
            disabled={(!replyText.trim() && !replyImage) || submitting}
            className="px-4 py-2 rounded-full bg-[color:var(--accent-color)] text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
          >
            {submitting ? t("posts.reply.sending") : t("posts.reply.send")}
          </button>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-sm w-full text-center">
            <h3 className="font-bold text-lg mb-2">{t("posts.deleteDialog.title")}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("posts.deleteDialog.body")}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-5 py-2 rounded-full text-sm font-medium bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
              >
                {t("posts.deleteDialog.cancel")}
              </button>
              <button
                onClick={handleDelete}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                {t("posts.deleteDialog.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReactorsSheet && post && (
        <ReactionDetailsSheet
          reactions={post.reactions}
          members={members}
          onClose={() => setShowReactorsSheet(false)}
        />
      )}
    </div>
  );
}
